import {createSelector} from 'reselect'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'

import {ATL_ORIGIN_LB, TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_RENT} from '../../const'
import {getStillLifeImages} from '../../sagas/lookbuilder/helpers'

const plannerView = (state) => get(state, 'lookbuilder.plannerView')

export const productsByCategory = (state) => get(plannerView(state), 'productsByCategory')
export const allProductCategories = (state) => get(plannerView(state), 'allProductCategories')
export const queuedProductSlugsToFetch = (state) =>
  get(plannerView(state), 'queuedProductSlugsToFetch')

/**
 * Get a single product by its category key
 * @param {Object} state The Redux state tree
 * @param {String} category The category key we want to check the product on
 */
export const getProductByCategory = (state, category) => get(productsByCategory(state), category)

/**
 * Returns a sorted list of product categories based on whether or not there
 * is a product selected for that category or not. Categories with selected
 * products are sorted to the top.
 * @return {String[]}
 */
export const allProductCategoriesSortedBySelection = createSelector(
  productsByCategory,
  allProductCategories,
  ($productsByCategory, $allProductCategories) => {
    return [...$allProductCategories].sort((a, b) => {
      const productA = $productsByCategory[a]
      const productB = $productsByCategory[b]
      if (!isEmpty(productA) && isEmpty(productB)) {
        return -1
      }
      if (isEmpty(productA) && !isEmpty(productB)) {
        return 1
      }
      return 0
    })
  },
)

/**
 * Get an object with only categories that have an associated product
 * @return {Object}
 */
export const filledProductsByCategory = createSelector(productsByCategory, ($productsByCategory) =>
  Object.entries($productsByCategory).reduce((output, [category, product]) => {
    if (isEmpty(product)) {
      return output
    }
    return {
      ...output,
      [category]: product,
    }
  }, {}),
)

/**
 * Creates an analytics-friendly object that contains only product names
 * (strings) as values.
 * @return {Object}
 */
export const filledProductsByCategoryForAnalytics = createSelector(
  filledProductsByCategory,
  ($filledProductsByCategory) =>
    Object.entries($filledProductsByCategory).reduce(
      (output, [category, {name}]) => ({
        ...output,
        [category]: name,
      }),
      {},
    ),
)

/**
 * Determine if there is currently more than one product in the Planner View
 * @return {Boolean}
 */
export const containsOneOrMoreProducts = createSelector(
  filledProductsByCategory,
  ($filledProductsByCategory) => Object.values($filledProductsByCategory).length >= 1,
)

/**
 * Normalize products in the Planner View into a format that the addToLook flow
 * will accept.
 * @return {Object[]} An array of product objects
 */
export const addToLookProducts = createSelector(
  filledProductsByCategory,
  ($filledProductsByCategory) =>
    Object.values($filledProductsByCategory).map((product) => ({
      categorySlug: get(product, 'setSpecs.defaultDisplayCategorySlug'),
      name: product.name,
      origin: ATL_ORIGIN_LB,
      setType: product.setType,
      slug: product.slug,
      transactionType: product.transactionType,
    })),
)

/**
 * Checks whether every product has a valid transaction type or not
 * @return {Boolean}
 */
export const allProductsHaveValidTransactionTypes = createSelector(
  filledProductsByCategory,
  ($filledProductsByCategory) => {
    if (isEmpty($filledProductsByCategory)) {
      return false
    }
    return Object.values($filledProductsByCategory).every(({transactionType}) =>
      [TRANSACTION_TYPE_BUY, TRANSACTION_TYPE_RENT].includes(transactionType),
    )
  },
)

const getProductFlatLayImage = (product) => {
  const defaultResult = {src: '', alt: ''}

  if (isEmpty(product)) {
    return defaultResult
  }

  const flatLayImages = getStillLifeImages(product)
  if (!flatLayImages.length) {
    return defaultResult
  }

  const {name: alt, url: src} = flatLayImages[0]
  return {alt, src}
}

export const getAddedProductsFlatLayImages = createSelector(
  productsByCategory,
  ($productsByCategory) => {
    const suitsTuxedosProduct = $productsByCategory['suits-tuxedos']
    const jacketProduct = isEmpty(suitsTuxedosProduct)
      ? {}
      : suitsTuxedosProduct.setChildren.find(({childSet}) =>
          childSet.tags.some(({name}) => name === 'jacket'),
        ).childSet
    const pantsProduct = isEmpty(suitsTuxedosProduct)
      ? {}
      : suitsTuxedosProduct.setChildren.find(({childSet}) =>
          childSet.tags.some(({name}) => name === 'pants'),
        ).childSet

    return {
      beltSuspendersImage: getProductFlatLayImage($productsByCategory['belts-suspenders']),
      jacketImage: getProductFlatLayImage(jacketProduct),
      neckwearImage: getProductFlatLayImage($productsByCategory.neckwear),
      pantsImage: getProductFlatLayImage(pantsProduct),
      pocketSquareImage: getProductFlatLayImage($productsByCategory['pocket-squares']),
      shirtImage: getProductFlatLayImage($productsByCategory.shirts),
      shoesImage: getProductFlatLayImage($productsByCategory.shoes),
      vestCummerbundImage: getProductFlatLayImage($productsByCategory['vests-cummerbunds']),
    }
  },
)

export const isLookEmpty = createSelector(filledProductsByCategory, ($filledProductsByCategory) =>
  isEmpty($filledProductsByCategory),
)
